<template>
  <div
    class="tabs_wrapper"
    :class="{
      'tabs_wrapper--loading': contentLoading,
      'tabs_wrapper--with-border': withBorder,
    }"
  >
    <div
      v-if="showNavigationArrows"
      class="nav-arrow nav-arrow--left"
      :class="{ 'nav-arrow--disabled': !canScrollLeft }"
      @click="scrollLeft"
    >
      <svg-loader
        src="/static/images/arrow-up.svg"
        class="nav-arrow__icon nav-arrow__icon--right"
      />
    </div>

    <div
      ref="tabsContainer"
      class="tabs_container"
      @scroll="handleScroll"
    >
      <md-button
        v-for="(tab, index) in tabs"
        :id="tab.id"
        :key="index"
        class="tab"
        :class="{ 'tab--active': tabSelected === index }"
        @click="switchTab(index, tab)"
      >
        <svg-loader
          v-if="tab.icon"
          :src="'/static/images/' + tab.icon"
          :class="tab.iconClass"
        />

        <span class="tab-name ellipsable">
          {{ tab.name }}
          <md-tooltip
            v-if="tab.tooltip"
            md-direction="top"
          >
            {{ tab.tooltip }}
          </md-tooltip>
        </span>

        <template
          v-if="
            tab.total !== undefined &&
            (tab.totalLoading || contentLoading || tab.total > 0)
          "
        >
          <span
            class="counter"
            :class="{ 'counter--active': tabSelected === index && tab.total > 0 }"
          >
            <template v-if="!tab.totalLoading && !contentLoading">
              <span v-if="tab.total > 0">{{ tab.total }}</span>
            </template>
            <LoadingDots v-else />
          </span>
        </template>
      </md-button>
    </div>

    <div
      v-if="showNavigationArrows"
      class="nav-arrow nav-arrow--right"
      :class="{ 'nav-arrow--disabled': !canScrollRight }"
      @click="scrollRight"
    >
      <svg-loader
        src="/static/images/arrow-up.svg"
        class="nav-arrow__icon nav-arrow__icon--left"
      />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/common/Spinner'
import LoadingDots from '@/components/common/LoadingDots'

export default {
  name: 'Tabs',
  components: {
    spinner: Spinner,
    LoadingDots,
  },
  props: {
    tabs: {
      type: Array,
      default: () => [],
    },
    tabSelected: {
      type: Number,
      default: 0,
    },
    contentLoading: {
      type: Boolean,
      default: false,
    },
    withBorder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showNavigationArrows: false,
      canScrollLeft: false,
      canScrollRight: false,
      scrollAmount: 200,
    }
  },
  watch: {
    tabs() {
      this.$nextTick(() => {
        this.checkOverflow()
      })
    },
  },
  mounted() {
    this.checkOverflow()
    window.addEventListener('resize', this.checkOverflow)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOverflow)
  },
  methods: {
    switchTab(tabIndex, tab) {
      if (this.tabSelected === tabIndex) return
      this.$emit('update:tabSelected', tabIndex)
      this.$emit('tabClicked', tabIndex)
      this.$emit('click', tab)

      this.$nextTick(() => {
        this.scrollToActiveTab(tabIndex)
      })
    },
    checkOverflow() {
      if (!this.$refs.tabsContainer) return

      const container = this.$refs.tabsContainer
      const hasOverflow = container.scrollWidth > container.clientWidth

      this.showNavigationArrows = hasOverflow
      this.updateScrollState()
    },
    updateScrollState() {
      if (!this.$refs.tabsContainer) return

      const container = this.$refs.tabsContainer
      this.canScrollLeft = container.scrollLeft > 0
      this.canScrollRight =
        container.scrollLeft < container.scrollWidth - container.clientWidth
    },
    scrollLeft() {
      if (!this.canScrollLeft) return

      const container = this.$refs.tabsContainer
      container.scrollBy({
        left: -this.scrollAmount,
        behavior: 'smooth',
      })
    },
    scrollRight() {
      if (!this.canScrollRight) return

      const container = this.$refs.tabsContainer
      container.scrollBy({
        left: this.scrollAmount,
        behavior: 'smooth',
      })
    },
    scrollToActiveTab(tabIndex) {
      if (!this.$refs.tabsContainer) return

      const container = this.$refs.tabsContainer
      const activeTab = container.children[tabIndex]

      if (activeTab) {
        const containerRect = container.getBoundingClientRect()
        const tabRect = activeTab.getBoundingClientRect()

        if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
          const scrollPosition =
            activeTab.offsetLeft - container.clientWidth / 2 + activeTab.clientWidth / 2

          container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth',
          })
        }
      }
    },
    handleScroll() {
      this.updateScrollState()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../static/stylesheets/variables';

.tabs_wrapper {
  display: flex;
  align-items: center;
  margin-top: 43px;
  gap: 8px;
  position: relative;

  &--loading {
    cursor: progress;

    .tab {
      pointer-events: none;
    }
  }

  &--with-border {
    .tabs_container {
      border-bottom: 1px solid #7f1a54;
    }
  }

  .nav-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;

    background-color: #e6e6e6;
    border-radius: 4px;
    height: 45px;
    min-width: 40px;
    font-weight: 600;

    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

    &__icon {
      cursor: pointer;
      font-size: 8px;
      width: 18px;
      min-width: 18px;
      height: 18px;
      min-height: 18px;

      &--left {
        transform: rotate(90deg);
      }

      &--right {
        transform: rotate(-90deg);
      }
    }
  }

  .tabs_container {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    flex: 1;
    position: relative;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .tab {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-shrink: 0;

      margin-right: 3px;
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 4px 4px 0px 0px;
      padding: 7px 25px;
      margin: 0px;
      cursor: pointer;

      font-family: Roboto, sans-serif;
      font-style: normal;
      font-weight: bold;
      font-size: 10px;
      line-height: 12px;
      text-align: center;
      letter-spacing: 1.33333px;
      text-transform: uppercase;
      color: #7a7a7a;
      min-height: 4em;
      white-space: nowrap;

      &:hover {
        background-color: #d5d5d7;
      }

      .tab-name {
        display: flex;
        align-items: center;
        position: relative;
      }

      .counter {
        padding: 2px 8px;
        min-width: 2.5em;
        background: #d3d2d2;
        border-radius: 8px;
        margin-left: 10px;

        &--active {
          background: white;
          border: 1px solid #7f1a54;
          color: #7f1a54;
        }
      }

      &--active {
        cursor: default;
        background-color: white;

        &:hover {
          color: inherit;
          background-color: white;
        }
      }

      .loader {
        width: 6px;
        height: 6px;
        margin-left: 10px;
      }
    }
  }
}

@media (max-width: 768px) {
  .tabs_wrapper {
    .nav-arrow {
      &__icon {
        width: 14px;
        min-width: 14px;
        height: 14px;
        min-height: 14px;
      }
    }

    .tabs_container .tab {
      padding: 7px 15px;
      font-size: 9px;
    }
  }
}
</style>
